import * as React from "react";
import { DragSource } from "react-dnd";
// Constants
import { KeyCode } from "@ms/uno-constants/lib/local/KeyCode";
// Controls
import { Checkbox } from "../checkbox/Checkbox";
import { TextField } from "../../textField/TextField";
import { TextFieldType } from "../../textField/TextField";
import { DndListItem } from "../dndList/DndListItem";
import { IconButton } from "../../iconButton/IconButton";
// Styles
import { uploadIconProps, deleteIconProps } from "../../../icons/icons.styles";
import { getClassNames, getStyles } from "./ChecklistItem.styles";
// Utilities
import { css } from "@fluentui/utilities/lib/css";
import { Strings } from "@ms/uno-resources/lib/local/CultureUtilities";
import { KeyboardUtilities } from "@ms/uno-utilities/lib/local/KeyboardUtilities";
//Models
import { ChecklistPermissions, isChecklistPermissionDisabled } from "@ms/uno-models/lib/local/client/permissions/ChecklistPermissions";
import { ChecklistItem as ChecklistItemModel } from "@ms/uno-models/lib/local/client/legacyPlanner/task/ChecklistItem";
export var ChecklistItemFocusState;
(function(ChecklistItemFocusState) {
    // Not focused on this checklist item
    ChecklistItemFocusState[ChecklistItemFocusState["None"] = 0] = "None";
    // Focused on the entire checklist item element
    ChecklistItemFocusState[ChecklistItemFocusState["ListItem"] = 1] = "ListItem";
    // Focused on the checklist item title for editing
    ChecklistItemFocusState[ChecklistItemFocusState["Title"] = 2] = "Title";
    // Focused on one of the promote/delete buttons
    ChecklistItemFocusState[ChecklistItemFocusState["Buttons"] = 3] = "Buttons";
})(ChecklistItemFocusState || (ChecklistItemFocusState = {}));
/** Task for converting ChecklistItem class component to Functional: https://office.visualstudio.com/PathWM/_workitems/edit/8394634 */ /** Standalone Checklist item component. Should be wrapped by a DndList */ class ChecklistItem extends DndListItem {
    /**
     * Name of the component
     * @override
     */ name() {
        return "ChecklistItem";
    }
    /**
     * @inheritdoc
     * @override
     */ getLiProps() {
        const { itemTypeString, listItem, isDragging, index } = this.props;
        const { focusState } = this.state;
        const componentClassNames = this.getClassNames();
        const listItemClassName = css(componentClassNames.root, isDragging ? "dragging" : "");
        return {
            className: listItemClassName,
            role: "checkbox",
            onFocus: this.onChecklistItemFocus,
            onBlur: this.onChecklistItemBlur,
            key: listItem.id,
            tabIndex: 0,
            ref: this.checklistItemElement,
            onKeyDown: this.preventDefaultAndStopPropagation,
            onKeyUp: this.onKeyUp,
            ["data-dnd-role"]: focusState === 2 ? undefined : itemTypeString,
            ["aria-checked"]: listItem.isChecked,
            ["data-index"]: index,
            draggable: focusState !== 2
        };
    }
    /**
     * @inheritdoc
     * @override
     */ renderListItem() {
        const { permissions, listItem: checklistItem, ariaLabel, theme } = this.props;
        const { editedTitle, focusState } = this.state;
        const disableEditTitle = isChecklistPermissionDisabled(permissions, ChecklistPermissions.DisableEditTitle);
        let componentClassNames = this.getClassNames(disableEditTitle);
        let itemTitle = /*#__PURE__*/ React.createElement("span", {
            className: componentClassNames.checklistItemTitle,
            onClick: disableEditTitle ? undefined : this.startChecklistItemTitleEdit,
            title: disableEditTitle ? Strings.SharedComponents_ReadonlyFieldPermissionsTooltip : checklistItem.title
        }, checklistItem.title);
        if (focusState === 2) {
            itemTitle = /*#__PURE__*/ React.createElement("form", {
                onSubmit: this.onFormSubmit,
                autoComplete: "off"
            }, /*#__PURE__*/ React.createElement(TextField, {
                className: "checklistTextField",
                placeholder: checklistItem.title,
                onChange: (event, newValue)=>{
                    this.checklistEditTitleChanged(newValue);
                },
                value: editedTitle,
                onBlur: this.onEditTitleBlur,
                autoFocus: true,
                styleType: TextFieldType.Text,
                maxLength: ChecklistItemModel.TitleMaxLength,
                onKeyDown: this.onEditTitleKeyDown,
                disabled: disableEditTitle
            }));
        }
        // When focus is on the checklistItem, show the checklist item commands (promote and delete).
        const commandsClassName = focusState !== 0 ? "forceShow" : "";
        const disableEditCheckbox = isChecklistPermissionDisabled(permissions, ChecklistPermissions.DisableCheckedStateToggling);
        const disablePromote = isChecklistPermissionDisabled(permissions, ChecklistPermissions.DisablePromote);
        const disableDelete = isChecklistPermissionDisabled(permissions, ChecklistPermissions.DisableDelete);
        const isReadOnly = disableDelete && disableEditCheckbox && disableEditTitle && disablePromote;
        if (isReadOnly !== disableEditTitle) {
            componentClassNames = this.getClassNames(isReadOnly);
        }
        // Command buttons are tabbable only in edit mode
        const buttonTabIndex = focusState === 2 ? 0 : -1;
        const componentJSX = /*#__PURE__*/ React.createElement("div", {
            draggable: focusState !== 2,
            className: componentClassNames.listItemContents
        }, /*#__PURE__*/ React.createElement(Checkbox, {
            title: disableEditCheckbox ? Strings.SharedComponents_ReadonlyFieldPermissionsTooltip : Strings.Checklist_MarkCompleteCommandTitle,
            onChange: this.onCheckboxChange,
            checked: checklistItem.isChecked,
            inputProps: {
                tabIndex: -1
            },
            ariaLabel: ariaLabel,
            disabled: disableEditCheckbox,
            appTheme: theme
        }), itemTitle, (!disablePromote || !disableDelete) && /*#__PURE__*/ React.createElement("div", {
            className: `${componentClassNames.checklistItemCommands} ${commandsClassName}`
        }, !disablePromote && /*#__PURE__*/ React.createElement(IconButton, {
            className: "checklistItem-promoteButton",
            iconProps: uploadIconProps,
            "aria-label": Strings.Checklist_PromoteItemCommandTitle,
            title: Strings.Checklist_PromoteItemCommandTitle,
            tabIndex: buttonTabIndex,
            onClick: this.onPromoteKeyUp,
            onKeyUp: this.onPromoteKeyUp,
            onFocus: this.onPromoteFocus,
            onBlur: this.onCommandButtonBlur,
            elementRef: this.promoteButtonRef,
            theme: theme
        }), !disableDelete && /*#__PURE__*/ React.createElement(IconButton, {
            className: "checklistItem-deleteButton",
            iconProps: deleteIconProps,
            "aria-label": Strings.Checklist_RemoveItemCommandTitle,
            title: Strings.Checklist_RemoveItemCommandTitle,
            tabIndex: buttonTabIndex,
            onClick: this.onDeleteKeyUp,
            onKeyUp: this.onDeleteKeyUp,
            onFocus: this.onDeleteFocus,
            onBlur: this.onCommandButtonBlur,
            elementRef: this.deleteButtonRef,
            theme: theme
        })));
        return componentJSX;
    }
    /**
     * Helper function to set focus on this element sometime after render is complete
     * @param prevProps Previous properties
     * @param prevState Previous state
     * @override
     */ componentDidUpdate(prevProps, prevState) {
        setTimeout(()=>{
            if (this.props.setFocusOnInit && this.checklistItemElement.current) {
                this.checklistItemElement.current.focus();
                this.props.clearFocusedChecklistItemId();
            }
        }, 0);
    }
    /**
     * Get the classnames associated with the computed styles for the checklist item component
     */ getClassNames(isReadOnly) {
        return getClassNames(getStyles(this.props.theme, this.props.styles, isReadOnly, this.props.listItem.isChecked));
    }
    /**
     * Initializes a new instance of ChecklistItem
     * @param props Props for the list item control
     * @param context Context to pass onto base component
     */ constructor(props, context){
        super(props, context), this.checklistItemElement = /*#__PURE__*/ React.createRef(), this.promoteButtonRef = /*#__PURE__*/ React.createRef(), this.deleteButtonRef = /*#__PURE__*/ React.createRef(), this.isFormSubmitted = false, /**
     * When the check state of the checklist item's checkbox is changed, call the callback in the props
     * @param evt the UI event
     */ this.onCheckboxChange = (evt)=>{
            this.props.onCheckboxClick(this.props.listItem.id);
        }, /**
     * Update focusState when list item is focused
     * @param evt The simple user interface events
     */ this.onChecklistItemFocus = (evt)=>{
            // This callback triggers even if children elements are focused
            // So, only change focus state if we are initially set to None
            if (this.state.focusState === 0) {
                this.setState({
                    focusState: 1
                });
            }
        }, /**
     * Update focusState when list item is blurred
     * @param evt The simple user interface events
     */ this.onChecklistItemBlur = (evt)=>{
            if (this.state.focusState === 1) {
                this.setState({
                    focusState: 0
                });
            }
        }, /**
     * Update a checklist item's title when checklist item is submitted by form
     * @param evt The form submit event
     */ this.onFormSubmit = (evt)=>{
            // We don't want the form to submit, so we prevent the default behavior
            evt.preventDefault();
            const { listItem, onRename } = this.props;
            const { editedTitle } = this.state;
            if (editedTitle !== "") {
                onRename(listItem.id, editedTitle);
            }
            if (evt.type === "submit") {
                // onFormSubmit is triggered onKeyDown with Enter button
                // The same Enter keypress will trigger this.onKeyUp(), which will attempt to put focus back into the Title textfield
                // So, we set this flag here to indicate we just submitted the form and don't want to re-focus on the Title textfield
                this.isFormSubmitted = true;
                // After submit, focus on the checklist item.
                this.checklistItemElement.current?.focus();
            }
        }, /**
     * Update a checklist item's title on blur of the checklist item
     * @param evt The focus event
     */ this.onEditTitleBlur = (evt)=>{
            const { listItem, onRename } = this.props;
            const { editedTitle } = this.state;
            if (editedTitle !== "" && !this.isFormSubmitted) {
                onRename(listItem.id, editedTitle);
            }
            const element = evt.relatedTarget;
            if (element === this.promoteButtonRef.current || element === this.deleteButtonRef.current) {
                // Focus changed to command buttons
                this.setState({
                    focusState: 3
                });
            } else if (element === this.checklistItemElement.current) {
                // Focus changed to list item
                this.setState({
                    focusState: 1
                });
            } else {
                // Focus changed to something else
                this.setState({
                    focusState: 0
                });
            }
        }, /**
     * Title for item being edited is updated
     * @param value Updated title for checklist item being edited
     */ this.checklistEditTitleChanged = (value)=>{
            this.setState({
                editedTitle: value
            });
        }, /**
     * Updates state to indicate we are editing the checklist item title text
     */ this.startChecklistItemTitleEdit = ()=>{
            this.setState({
                focusState: 2,
                editedTitle: this.props.listItem.title
            });
        }, /**
     * Callback when keydown event happens during checklist item name edit
     * This stop propagation for space events allows users to add spaces when editing
     * the checklist item title
     * @param evt The keyboard event
     */ this.onEditTitleKeyDown = (evt)=>{
            if (KeyboardUtilities.getKey(evt) === KeyCode.SPACE) {
                evt.stopPropagation();
            }
        }, /**
     * Process and handle keyboard commands on the checklist item
     * @param evt The ui event to process
     */ this.onKeyUp = (evt)=>{
            evt.preventDefault();
            const { permissions, listItem, onCheckboxClick, onDelete, onMove } = this.props;
            const { focusState } = this.state;
            const element = evt.target;
            const keyEvt = evt;
            const keyPressed = KeyboardUtilities.getKey(keyEvt);
            if (element.className.indexOf("Upload") > -1 && !keyEvt.shiftKey) {
                return;
            }
            const disableDelete = isChecklistPermissionDisabled(permissions, ChecklistPermissions.DisableDelete);
            const disableEditCheckbox = isChecklistPermissionDisabled(permissions, ChecklistPermissions.DisableCheckedStateToggling);
            const disableEditTitle = isChecklistPermissionDisabled(permissions, ChecklistPermissions.DisableEditTitle);
            const disableReorder = isChecklistPermissionDisabled(permissions, ChecklistPermissions.DisableReorder);
            // Edit mode
            // Only use Enter to trigger edit mode if we're focused on ListItem.
            // On MacOS using the Zhuyin traditional chinese keyboard, the enter for selecting a character causes
            // the changes to the checklist item to be lost without this check as the title is reset.
            if (keyPressed === KeyCode.ENTER && focusState === 1 && !disableEditTitle) {
                if (this.isFormSubmitted) {
                    // We just pressed Enter to submit the form for edit Title
                    // So we don't want to start editing again
                    this.isFormSubmitted = false;
                } else {
                    // Handle start editing logic inside a setTimeout,
                    // with this we will always render the form element and text field after the current event is completed,
                    // otherwise we see issues in Edge browser where enter key event and input text focus happens almost at same time
                    // and it results on form onSubmit event.
                    setTimeout(()=>{
                        // start editing
                        this.startChecklistItemTitleEdit();
                    }, 0);
                }
            } else if (keyPressed === KeyCode.DELETE && element.tagName !== "INPUT" && !disableDelete) {
                // Delete the selected item
                onDelete(listItem.id);
            } else if (keyPressed === KeyCode.RIGHT_ARROW && element.tagName === "LI") {
                // Focus is on checklist item + NO editing.
                // Move focus to promote/delete icon
                this.focusFirstCommandButton();
            } else if (keyPressed === KeyCode.SPACE && element.tagName !== "INPUT" && !disableEditCheckbox) {
                // Focus is on checklist item + NO editing.
                // toggle checked
                onCheckboxClick(listItem.id);
            } else if (keyPressed === KeyCode.UP_ARROW && keyEvt.ctrlKey && element.tagName !== "INPUT" && !disableReorder) {
                // Ctrl + Up + [Non-Edit mode] => Move item up
                onMove(listItem, -1);
            } else if (keyPressed === KeyCode.DOWN_ARROW && keyEvt.ctrlKey && element.tagName !== "INPUT" && !disableReorder) {
                // Ctrl + Down + [Non-Edit mode] => Move item down
                onMove(listItem, 1);
            }
        }, /**
     * When the delete command gains focus, we want the commands to continue to be shown
     * @param evt The UI event
     */ this.onDeleteFocus = (evt)=>{
            this.setState({
                focusState: 3
            });
        }, /**
     * When the promote command gains focus, we want the commands to continue to be shown
     * @param evt The UI event
     */ this.onPromoteFocus = (evt)=>{
            this.setState({
                focusState: 3
            });
        }, /**
     * When a command button loses focus, set focus state to None.
     */ this.onCommandButtonBlur = ()=>{
            this.setState({
                focusState: 0
            });
        }, /**
     * Handle key presses on the promote button
     * @param evt The UI event
     */ this.onPromoteKeyUp = (evt)=>{
            const { onPromote, listItem } = this.props;
            evt.preventDefault();
            if (KeyboardUtilities.isButtonPress(evt)) {
                onPromote(listItem.id);
            }
            const keyPressed = KeyboardUtilities.getKey(evt);
            // If right arrow is pressed
            if (keyPressed === KeyCode.RIGHT_ARROW) {
                this.deleteButtonRef.current?.focus();
            }
        }, /**
     * Handle key presses on the delete button
     * @param evt The UI event
     */ this.onDeleteKeyUp = (evt)=>{
            const { onDelete, listItem } = this.props;
            evt.preventDefault();
            // Stop propagation to avoid delete getting overridden by check/uncheck operation
            evt.stopPropagation();
            if (KeyboardUtilities.isButtonPress(evt)) {
                onDelete(listItem.id);
            }
            const keyPressed = KeyboardUtilities.getKey(evt);
            // If right arrow is pressed
            if (keyPressed === KeyCode.LEFT_ARROW) {
                this.promoteButtonRef.current?.focus();
            }
        }, /**
     * Focus on the first command button such as promote/delete
     */ this.focusFirstCommandButton = ()=>{
            const { permissions } = this.props;
            const disablePromote = isChecklistPermissionDisabled(permissions, ChecklistPermissions.DisablePromote);
            const disableDelete = isChecklistPermissionDisabled(permissions, ChecklistPermissions.DisableDelete);
            if (!disablePromote) {
                this.promoteButtonRef.current?.focus();
            } else if (!disableDelete) {
                this.deleteButtonRef.current?.focus();
            }
        }, /**
     * Just prevent default and stop propagation for space bar presses. This keeps the page from scrolling down and from toggling check mark when pressing space on the buttons
     */ this.preventDefaultAndStopPropagation = (evt)=>{
            if (KeyboardUtilities.getKey(evt) === KeyCode.SPACE) {
                evt.preventDefault();
                evt.stopPropagation();
            }
        };
        this.state = {
            editedTitle: "",
            focusState: 0
        };
    }
}
export default DragSource((props)=>props.itemTypeString, ChecklistItem.prototype.itemSource(), ChecklistItem.prototype.itemCollect)(ChecklistItem);
